/**
 * Component for adding a new environment.
 * Handles form submission, duplicate validation, and emits events to update the environment list.
 */
import { Component, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { EnvironmentService } from '../environment.service';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { EnvironmentFormComponent } from '../environment-form/environment-form.component';
import { ReleaseMgmtService } from '../../../shared/services/releaseMgmtService';
import { LoaderService } from '../../../shared/services/loader.service';

@Component({
  standalone:true,
  imports:[CommonModule, EnvironmentFormComponent,],
  selector: 'app-add-environment',
  templateUrl: './add-environment.component.html',
  styleUrls: ['./add-environment.component.scss']
})
export class AddEnvironmentComponent implements OnDestroy {
  /** Emits when a new environment is successfully added */
  @Output() environmentAdded = new EventEmitter<any>();
    /** Emits when the add environment overlay should be closed */
  @Output() close: EventEmitter<any> = new EventEmitter();
  /** List of all existing environments for duplicate validation */
  environmentList: any[] = [];
  private environmentsSubscription: Subscription;
  availableServices: any[] = [];
  private servicesSubscription: Subscription;
  loading: boolean;
  @ViewChild(EnvironmentFormComponent) child!: EnvironmentFormComponent;


  /**
   * Fetches the list of environments for duplicate validation.
   */
  constructor(
    private environmentService: EnvironmentService,
    private toastr: ToastrService,
    private ReleaseMgmtService: ReleaseMgmtService,
    private loaderService: LoaderService,
  ) {
    this.environmentsSubscription = this.environmentService.getEnvironments().subscribe((data: any[]) => {
      this.environmentList = data || [];
    });
  }

  ngOnInit() {
    // Fetch available services for add mode
    this.servicesSubscription = this.ReleaseMgmtService.apiGetActiveServices().subscribe((services: any[]) => {
      this.availableServices = services || [];
    });
  }

  ngOnDestroy() {
    if (this.environmentsSubscription) {
      this.environmentsSubscription.unsubscribe();
    }
    if (this.servicesSubscription) {
      this.servicesSubscription.unsubscribe();
    }
  }

  closePop() {
    this.close.emit();
  }

  /**
   * Handles form submission for adding a new environment.
   * Performs duplicate validation and emits events on success.
   * @param formValue The form data submitted by the user
   */
  handleFormSubmit(formValue: any) {
    // Duplicate validation
    this.setLoader();
  const environmentNameExists = this.environmentList.some(env =>
      typeof env.environmentName === 'string' &&
      env.environmentName.trim().toLowerCase() === formValue.environmentName.trim().toLowerCase()
    );
    const workflowNameExists = this.environmentList.some(env =>
      typeof env.workflowName === 'string' &&
      env.workflowName.trim().toLowerCase() === formValue.workflowName.trim().toLowerCase()
    );
    if (environmentNameExists || workflowNameExists) {
      let message = '';
      if (environmentNameExists) message += 'Environment name exists!';
      if (environmentNameExists && workflowNameExists) message += ' , ';
      if (workflowNameExists) message += 'Workflow Name exists!';
      this.toastr.error(message, 'Error');
      this.setUnLoader();
      return null;
    } else {

      // Create a new environment object with the form data
      const newEnvironment = {
        id: formValue.id || undefined, // let backend assign if not present
        environmentName: formValue.environmentName,
        environmentType: formValue.environmentType,
        workflowName: formValue.workflowName,
        status: formValue.isActive ? 'Active' : 'Inactive',
        services: (formValue.services as any[]).map((service: any, index: number) => {
          let serviceId = service.id;
          if (!serviceId) {
            // Try to find by serviceName in availableServices
            const match = this.availableServices.find(
              (s: any) =>
                (service.serviceName && s.serviceName === service.serviceName) ||
                (service.name && s.serviceName === service.name)
            );
            serviceId = match ? match.id : undefined;
          }
          return {
            serviceId,
            serviceName: service.serviceName || service.name || this.getServiceName(index),
            hasApi: Boolean(service.hasApi),
            hasWeb: Boolean(service.hasWeb),
            apiUrl: service.apiUrl,
            webUrl: service.webUrl,
            isSelected: service.selected || service.isSelected || false
          };
        }),
      };

     this.environmentService.addEnvironment(newEnvironment).subscribe({
        next: (response) => {
          if (response !== null) {
             this.toastr.success('Environment added successfully!');
            this.environmentAdded.emit();
            this.close.emit();
            this.child.onClear();                       
          }
          this.setUnLoader();
        },
        error: () => {
          this.toastr.error('Error occured while adding new environment.');
          this.setUnLoader();
        }
      });
    }
  }

 /**

   * Returns the display name for a service by index.
   * @param index Index of the service
   */
  private getServiceName(index: number): string {
    if (this.availableServices && this.availableServices[index] && this.availableServices[index].serviceName) {
      return this.availableServices[index].serviceName;
    }
    return `Service ${index + 1}`;

  }

  setLoader() {
    this.loading = true;
    this.loaderService.showSpinner.next(true);
  }
  setUnLoader() {
    this.loading = false;
    this.loaderService.showSpinner.next(false);
  }
}
